import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Label, Text, Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, Title3, Subtitle2, Subtitle1, Avatar, InfoLabel, MessageBar, MessageBarBody, MessageBarTitle, Checkbox, Spinner, Badge, Divider, DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, TableCellLayout, TableColumnDefinition, createTableColumn } from '@fluentui/react-components';
import { CalendarToday28Regular, Dismiss24Regular } from '@fluentui/react-icons';
import moment from 'moment';

// component functions
import * as ComponentFunctions from './ConsumptionDetails.Functions';

// styles
import './ConsumptionDetails.Styles.css';
import ConsumptionListComponent from '../ConsumptionList/ConsumptionList.Component';
import ContractConceptSelectorComponent from '../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component';
import { ContractConcept } from '../../../models/Entities/Contracts/ContractConcept';
import PractitionerSelectorComponent from '../../../commons/component/Inputs/PractitionerSelector/PractitionerSelectorComponent';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { Provider } from '../../../models/Entities/Provider/Provider';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';

// components
import ConsumptionDetailMenuComponent from './ConsumptionDetailsMenu/ConsumptionDetailMenu.Component';
import ConsumptionAmountComponent from '../ConsumptionAmount/ConsumptionAmount.component';
import { ConsumptionStatus } from '../../../commons/component/Inputs/ProtocolConsumptionStatusSelector/ProtocolConsumptionStatusSelector.Functions';
import ProtocolConsumptionStatusSelectorComponent from '../../../commons/component/Inputs/ProtocolConsumptionStatusSelector/ProtocolConsumptionStatusSelector.Component';
import MedicalServiceMultiSelectorComponent from '../../MedicalServices/MedicalServiceSelector/MedicalServiceMultiSelectorComponent';
import MedicalServiceSelectorComponent from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent';
import ProviderSelectorComponent from '../../../commons/component/Inputs/ProviderSelector/ProviderSelectorComponent';
import ConsumptionSettlementComponent from '../ConsumptionSettlement/Consumption.Settlement.Component';
import ChangeTypeOfConsumptionComponent from '../ChangeTypeOfConsumption/ChangeTypeOfConsumption.Component';
import VisitConceptFormComponent from '../VisitConceptForm/VisitConcept.Form.Component';
import ContractStagesSelectorComponent from '../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import ChildConsumptionListComponent from '../ChildConsumptionList/ChildConsumption.List.Component';


// ─── DataGrid value table types & helpers ────────────────────────────────────

interface ValueRow {
    id: string;
    concept: React.ReactNode;
    unitValue: React.ReactNode;
    coefficient: string;
    quantity: string;
    totalByQuantity: string;
    percentage: string;
    owner: string;
    team: string;
    total: string;
    rowKind: 'main' | 'withholding' | 'additional' | 'cost' | 'section';
}

const fmtCurrency = (amount: number | undefined, currency: string | undefined): string =>
    (amount ?? 0).toLocaleString('es-AR', {
        style: 'currency',
        currency: (currency ?? 'ARS').toUpperCase(),
    });

const valueColumns: TableColumnDefinition<ValueRow>[] = [
    createTableColumn<ValueRow>({
        columnId: 'concept',
        renderHeaderCell: () => 'Concepto',
        renderCell: (item) => <TableCellLayout>{item.concept}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'unitValue',
        renderHeaderCell: () => 'Valor unitario',
        renderCell: (item) => <TableCellLayout>{item.unitValue}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'coefficient',
        renderHeaderCell: () => 'Coef.',
        renderCell: (item) => <TableCellLayout>{item.coefficient}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'quantity',
        renderHeaderCell: () => 'Cantidad',
        renderCell: (item) => <TableCellLayout>{item.quantity}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'totalByQuantity',
        renderHeaderCell: () => 'Total',
        renderCell: (item) => <TableCellLayout>{item.totalByQuantity}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'percentage',
        renderHeaderCell: () => '%',
        renderCell: (item) => <TableCellLayout>{item.percentage}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'owner',
        renderHeaderCell: () => 'Centro',
        renderCell: (item) => <TableCellLayout>{item.owner}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'team',
        renderHeaderCell: () => 'Equipo médico',
        renderCell: (item) => <TableCellLayout>{item.team}</TableCellLayout>,
    }),
    createTableColumn<ValueRow>({
        columnId: 'total',
        renderHeaderCell: () => 'Importe',
        renderCell: (item) => <TableCellLayout><strong>{item.total}</strong></TableCellLayout>,
    }),
];

// ─────────────────────────────────────────────────────────────────────────────

const PanelInfo = (props: ComponentFunctions.Props) => {

    const [changeConceptOptionActive, setChangeConceptOptionActive] = useState(false);
    const [changeProducerOptionActive, setChangeProducerOptionActive] = useState(false);
    const [changeProviderOptionActive, setChangeProviderOptionActive] = useState(false);
    const [changeStageOptionActive, setChangeStageOptionActive] = useState(false);
    const [changeMedicalServiceOptionActive, setChangeMedicalServiceOptionActive] = useState(false);
    const [ChangeAll, setChangeAll] = useState<boolean>(false);
    const [changeProcedureOptionActive, setChangeProcedureOptionActive] = useState(false);
    const [changeAdditionalOptionActive, setChangeAdditionalOptionActive] = useState(false);

    const [newConceptAssociation, setNewConceptAssociation] = useState<ContractConcept>();
    const [newProducer, setNewProducer] = useState<Practitioner>();
    const [newProvider, setNewProvider] = useState<Provider>();
    const [newMedicalService, setNewMedicalService] = useState<AvailableMedicalService>();
    const [newStage, setNewStage] = useState<ContractStage>();

    useEffect(() => {
        if (props.Consumption?.protocolId) {
            props.GetAllProtocolContractAssociations(props.Consumption?.protocolId);
        }
    }, [props.Consumption?.protocolId]);

    useEffect(() => {
        setChangeConceptOptionActive(props.isChangingConcept);
        setChangeAll(false);
    }, [props.isChangingConcept]);

    useEffect(() => {
        setChangeProducerOptionActive(props.isChangingProducer);
    }, [props.isChangingProducer]);

    useEffect(() => {
        setChangeProviderOptionActive(props.isChangingProvider);
    }, [props.isChangingProvider]);

    useEffect(() => {
        setChangeMedicalServiceOptionActive(props.isChangingMedicalService);
    }, [props.isChangingMedicalService]);

    // Close stage change panel when update succeeded
    useEffect(() => {
        if (changeStageOptionActive && props.Consumption?.isUpdatedSuccessfully) {
            setChangeStageOptionActive(false);
            setNewStage(undefined);
        }
    }, [props.Consumption?.isUpdatedSuccessfully]);

    // Close procedure change panel when update succeeded
    useEffect(() => {
        if (changeProcedureOptionActive && props.Consumption?.isUpdatedSuccessfully) {
            setChangeProcedureOptionActive(false);
            setChangeAll(false);
        }
    }, [props.Consumption?.isUpdatedSuccessfully]);

    // Close additional change panel when update succeeded
    useEffect(() => {
        if (changeAdditionalOptionActive && props.Consumption?.isUpdatedSuccessfully) {
            setChangeAdditionalOptionActive(false);
            setChangeAll(false);
        }
    }, [props.Consumption?.isUpdatedSuccessfully]);

    const HandleChangeConcept = () => {
        if (props.ProtocolId && props.Consumption?.id && newConceptAssociation?.id) {
            props.ChangeContractConcept(props.ProtocolId, props.Consumption?.id, newConceptAssociation.id, ChangeAll);
        }
    };

    const HandleChangeProducer = () => {
        if (props.ProtocolId && props.Consumption?.id && newProducer?.userId) {
            props.ChangeProducer(props.ProtocolId, props.Consumption?.id, newProducer.userId);
        }
    };

    const HandleChangeProvider = () => {
        if (props.ProtocolId && props.Consumption?.id && newProvider) {
            newProvider && props.ChangeProvider(props.ProtocolId, props.Consumption?.id, newProvider);
        }
    };

    const HandleChangeMedicalService = () => {
        if (props.ProtocolId && props.Consumption?.id && newMedicalService?.id) {
            props.ChangeMedicalService(props.ProtocolId, props.Consumption?.id, newMedicalService?.id);
        }
    };

    const HandleChangeStage = () => {
        if (props.Consumption && newStage?.id) {
            props.ChangeVisit(props.Consumption.protocolId, props.Consumption.id, newStage.id);
        }
    };

    const HandleSetProcedure = (isProcedure: boolean) => {
        if (props.ProtocolId && props.Consumption?.id) {
            props.ChangeProcedure(props.ProtocolId, props.Consumption.id, isProcedure, ChangeAll);
        }
    };

    const HandleSetAdditional = (isAdditional: boolean) => {
        if (props.ProtocolId && props.Consumption?.id) {
            props.ChangeAdditional(props.ProtocolId, props.Consumption.id, isAdditional, ChangeAll);
        }
    };

    const getStatusColor = (status: number): 'brand' | 'danger' | 'important' | 'informative' | 'severe' | 'subtle' | 'success' | 'warning' => {
        switch (status) {
            case 2: return 'success';
            case 3: return 'success';
            case 4: return 'danger';
            case 5: return 'subtle';
            default: return 'warning';
        }
    };

    const valueRows = useMemo((): ValueRow[] => {
        const c = props.Consumption;
        if (!c) return [];

        const mainRow: ValueRow = {
            id: 'main',
            concept: c.haveOverheadIncluded ? (
                <InfoLabel
                    info={<>Se descuenta overhead ({c.overheadPercentage}%)&nbsp;{fmtCurrency(c.overheadAmount, c.currencyCode)}</>}
                >
                    Valor unitario del concepto
                </InfoLabel>
            ) : 'Valor unitario del concepto',
            unitValue: (
                <div>
                    <ConsumptionAmountComponent ProtocolId={props.ProtocolId} ProtocolConsumptionId={c.id} />
                    <small className="overheadLabel">overhead: {fmtCurrency(c.overheadAmount, c.currencyCode)}</small>
                </div>
            ),
            coefficient: String(c.coefficientApplied ?? ''),
            quantity: String(c.quantity ?? ''),
            totalByQuantity: fmtCurrency(c.amount, c.currencyCode),
            percentage: '',
            owner: `(${c.feeDivisionPercentage === 0 ? '100' : c.feeDivisionPercentage}%) ${fmtCurrency(c.ownerAmount, c.currencyCode)}`,
            team: `(${c.teamDivisionPercentage}%) ${fmtCurrency(c.teamAmount, c.currencyCode)}`,
            total: fmtCurrency(c.amount, c.currencyCode),
            rowKind: 'main',
        };

        const withholdingRow: ValueRow = {
            id: 'withholding',
            concept: 'WithHolding',
            unitValue: fmtCurrency(c.withHolingAmount, c.currencyCode),
            coefficient: '1',
            quantity: '',
            totalByQuantity: '',
            percentage: (c.withHolingPercentage ?? 0) + '%',
            owner: `(${c.feeDivisionPercentage}%) ${fmtCurrency(c.ownerAmountWithholding, c.currencyCode)}`,
            team: `(${c.teamDivisionPercentage}%) ${fmtCurrency(c.teamAmountWithholding, c.currencyCode)}`,
            total: fmtCurrency(c.withHolingAmount, c.currencyCode),
            rowKind: 'withholding',
        };

        const additionalRows: ValueRow[] = (c.conceptValues?.length ?? 0) > 0 ? [
            { id: 'section-additional', concept: 'Montos Adicionales', unitValue: '', coefficient: '', quantity: '', totalByQuantity: '', percentage: '', owner: '', team: '', total: '', rowKind: 'section' },
            ...c.conceptValues.map((concept): ValueRow => ({
                id: `additional-${concept.id}`,
                concept: concept.contractConceptName,
                unitValue: `(${concept.percentValue}%) ${fmtCurrency(concept.value, c.currencyCode)}`,
                coefficient: '1',
                quantity: '1',
                totalByQuantity: fmtCurrency(concept.value, c.currencyCode),
                percentage: concept.percentValue + '%',
                owner: `(${concept.feeDivisionPercentage === 0 ? 100 : concept.feeDivisionPercentage}%) ${fmtCurrency(concept.ownerAmount, c.currencyCode)}`,
                team: `(${concept.teamDivisionPercentage}%) ${fmtCurrency(concept.teamAmount, c.currencyCode)}`,
                total: fmtCurrency(concept.value, c.currencyCode),
                rowKind: 'additional',
            })),
        ] : [];

        const costRows: ValueRow[] = [
            { id: 'section-cost', concept: 'Costos', unitValue: '', coefficient: '', quantity: '', totalByQuantity: '', percentage: '', owner: '', team: '', total: '', rowKind: 'section' },
            {
                id: 'cost',
                concept: 'Costo calculado',
                unitValue: fmtCurrency(c.costAmount, c.costCurrencyCode ?? 'ARS'),
                coefficient: '1',
                quantity: '',
                totalByQuantity: '',
                percentage: '',
                owner: '',
                team: '',
                total: fmtCurrency(c.costAmount, c.costCurrencyCode ?? 'ARS'),
                rowKind: 'cost',
            },
        ];

        return [mainRow, withholdingRow, ...additionalRows, ...costRows];
    }, [props.Consumption, props.ProtocolId]);

    return (
        <div>
            {props.ProtocolConsumptionId && <ConsumptionDetailMenuComponent ProtocolId={props.ProtocolId} ProtocolConsumptionId={props.ProtocolConsumptionId} />}
            <div className='consumptionDetails'>
                <div className="infoField">
                    <Label>Paciente</Label>
                    <Text>{props.Consumption?.patientNumberInProtocol}</Text>
                </div>
                <div className="infoField">
                    <Label>Fecha de realización</Label>
                    <Text>{moment(props.Consumption?.serviceDueDate).format('DD/MM/YYYY hh:mm a')}</Text>
                </div>
                <div className="infoField">
                    <Label>Estado</Label>
                    <Badge color={getStatusColor(props.Consumption!.status)} appearance="tint" size="medium">
                        {ComponentFunctions.GetStatusDescription(props.Consumption!.status)}
                    </Badge>
                </div>
                {props.Consumption?.settlementId && <div className="infoField" style={{ gridColumn: '1 / -1' }}><ConsumptionSettlementComponent ProtocolId={props.ProtocolId} ProtocolConsumptionId={props.Consumption.id} /></div>}
            </div>

            <Divider className="section-divider" />
            <Subtitle2 className="section-title">Clasificación y servicio</Subtitle2>
            <div className='RelatedConceptContainer'>
                {props.ProtocolConsumptionId && <div>
                    <ChangeTypeOfConsumptionComponent ProtocolConsumptionId={props.ProtocolConsumptionId} ProtocolId={props.ProtocolId} />
                </div>}
                {/* Stage (visit) change */}
                {props.ContractId && !changeStageOptionActive && (
                    <div className="infoRow">
                        <Label>Visita</Label>
                        <Text className={props.Consumption?.stageName ? 'infoValue' : 'infoValueEmpty'}>
                            {props.Consumption?.stageName || 'Sin visita asociada'}
                        </Text>
                        <div className="buttonGroup">
                            <Button appearance="outline" onClick={() => setChangeStageOptionActive(true)}>Cambiar visita</Button>
                        </div>
                    </div>
                )}
                {/* Procedure flag */}
                {!changeProcedureOptionActive && (
                    <div className="infoRow">
                        <Label>Marcar como procedimiento</Label>
                        <div className="buttonGroup">
                            <Button appearance="outline" onClick={() => setChangeProcedureOptionActive(true)}>Cambiar</Button>
                        </div>
                    </div>
                )}
                {changeProcedureOptionActive && (
                    <div className="editPanel">
                        <Checkbox
                            checked={ChangeAll}
                            onChange={(ev, data) => setChangeAll(!!data.checked)}
                            label="Aplicar a todos los consumos de este tipo"
                        />
                        <div className="buttonGroup">
                            <Button appearance="primary" onClick={() => HandleSetProcedure(true)}>Marcar</Button>
                            <Button appearance="outline" onClick={() => HandleSetProcedure(false)}>Desmarcar</Button>
                            <Button appearance="subtle" onClick={() => setChangeProcedureOptionActive(false)}>Cancelar</Button>
                        </div>
                    </div>
                )}

                {/* Additional flag */}
                {!changeAdditionalOptionActive && (
                    <div className="infoRow">
                        <Label>Marcar como adicional</Label>
                        <div className="buttonGroup">
                            <Button appearance="outline" onClick={() => setChangeAdditionalOptionActive(true)}>Cambiar</Button>
                        </div>
                    </div>
                )}
                {changeAdditionalOptionActive && (
                    <div className="editPanel">
                        <Checkbox
                            checked={ChangeAll}
                            onChange={(ev, data) => setChangeAll(!!data.checked)}
                            label="Aplicar a todos los consumos de este tipo"
                        />
                        <div className="buttonGroup">
                            <Button appearance="primary" onClick={() => HandleSetAdditional(true)}>Marcar</Button>
                            <Button appearance="outline" onClick={() => HandleSetAdditional(false)}>Desmarcar</Button>
                            <Button appearance="subtle" onClick={() => setChangeAdditionalOptionActive(false)}>Cancelar</Button>
                        </div>
                    </div>
                )}
                {props.ContractId && changeStageOptionActive && (
                    <div className="editPanel">
                        {props.Consumption?.failOnUpdating && (
                            <MessageBar key={props.Consumption?.id} intent={'error'}>
                                <MessageBarBody>
                                    <MessageBarTitle>Error</MessageBarTitle>
                                    {props.Consumption?.error?.ErrorMessage}
                                </MessageBarBody>
                            </MessageBar>
                        )}
                        <ContractStagesSelectorComponent
                            ContractId={props.ContractId}
                            onSelectedContractStage={(stages: ContractStage[]) => setNewStage(stages?.[0])}
                            ContractStagesSelected={newStage ? [newStage.id] : props.Consumption?.stageId ? [props.Consumption?.stageId] : []}
                            MultiselectEnable={false}
                            noteText={undefined}
                        />
                        <div className="buttonGroup">
                            <Button appearance="primary" disabled={props.Consumption?.isUpdating || newStage === undefined} onClick={HandleChangeStage}>
                                {props.Consumption?.isUpdating ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                            </Button>
                            <Button appearance="subtle" disabled={props.Consumption?.isUpdating} onClick={() => setChangeStageOptionActive(false)}>Cancelar</Button>
                        </div>
                    </div>
                )}
                {!changeMedicalServiceOptionActive && <div className="infoRow">
                    {props.Consumption?.medicalServiceId ? <>
                        <Label>Servicio Médico asociado</Label>
                        <Text className="infoValue">{props.Consumption?.medicalServiceName}</Text>
                    </>
                        : <>
                            <Label>Servicio Médico asociado</Label>
                            <Text className="infoValueEmpty">Este consumo no está asociado a ningún servicio médico</Text>
                        </>}
                    <div className="buttonGroup">
                        <Button appearance="outline" onClick={() => setChangeMedicalServiceOptionActive(true)}>{props.Consumption?.medicalServiceId ? 'Cambiar servicio médico' : 'Asociar servicio médico'}</Button>
                    </div>
                </div>}
                {changeMedicalServiceOptionActive &&
                    <div className="editPanel">
                        {props.failChangingMedicalService && <MessageBar key={props.Consumption?.id} intent={"error"}>
                            <MessageBarBody>
                                <MessageBarTitle>Error</MessageBarTitle>
                                {props.errorChangingMedicalService?.ErrorMessage || props.errorChangingMedicalService?.Errors?.join(', ')}
                            </MessageBarBody>
                        </MessageBar>}
                        <MedicalServiceSelectorComponent
                            onSelectedServices={(services: AvailableMedicalService[] | undefined) => services ? setNewMedicalService(services[0]) : setNewMedicalService(undefined)}
                            MedicalServicesSelectedIds={newMedicalService ? [newMedicalService.id] : props.Consumption?.medicalServiceId ? [props.Consumption?.medicalServiceId] : []}
                            MultiselectEnable={false} />
                        <div className="buttonGroup">
                            <Button appearance="primary" disabled={props.isChangingMedicalService || newMedicalService === undefined} onClick={HandleChangeMedicalService}>
                                {props.isChangingMedicalService ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                            </Button>
                            <Button appearance="subtle" disabled={props.isChangingMedicalService} onClick={() => setChangeMedicalServiceOptionActive(false)}>Cancelar</Button>
                        </div>
                    </div>
                }
            </div>
            <Divider className="section-divider" />
            <Subtitle2 className="section-title">Proveedor</Subtitle2>
            <div className='RelatedConceptContainer'>
                {props.failChangingProvider && <section><MessageBar key={props.Consumption?.id} intent={"error"}>
                    <MessageBarBody>
                        <MessageBarTitle>{props.errorChangingProvider?.ErrorMessage}</MessageBarTitle>
                        Verifique los datos ingresados
                    </MessageBarBody>
                </MessageBar></section>}
                {!changeProviderOptionActive && <div className="infoRow">
                    {props.Consumption?.providerId && <>
                        <Text className="infoValue">{props.Consumption?.providerName}</Text>
                    </>}
                    {!props.Consumption?.providerId && <>
                        <Text className="infoValueEmpty">Este consumo no está asociado a ningún proveedor</Text>
                    </>}
                    <div className="buttonGroup">
                        <Button appearance="outline" onClick={() => setChangeProviderOptionActive(true)}>{props.Consumption?.protocolId ? 'Cambiar proveedor' : 'Asociar proveedor'}</Button>
                    </div>
                </div>}
                {changeProviderOptionActive &&
                    <div className="editPanel">
                        <ProviderSelectorComponent
                            onChangeSelection={(provider: Provider[] | undefined) => provider ? setNewProvider(provider[0]) : setNewProvider(undefined)}
                            ProviderSelected={newProvider ? [newProvider.person.id] : props.Consumption?.providerId ? [props.Consumption?.providerId] as string[] : []}
                            MultiselectEnable={false} />
                        <div className="buttonGroup">
                            <Button appearance="primary" disabled={props.isChangingProvider || newProvider === undefined} onClick={HandleChangeProvider}>
                                {props.isChangingProvider ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                            </Button>
                            <Button appearance="subtle" disabled={props.isChangingProvider} onClick={() => setChangeProviderOptionActive(false)}>Cancelar</Button>
                        </div>
                    </div>
                }
            </div>
            <Divider className="section-divider" />
            <Subtitle2 className="section-title">Realizado por</Subtitle2>
            <div className='RelatedConceptContainer'>
                {!changeProducerOptionActive && <div className='consumptionPerson'>
                    {props.failChangingProducer && <MessageBar key={props.Consumption?.id} intent={"error"}>
                        <MessageBarBody>
                            <MessageBarTitle>Error</MessageBarTitle>
                            {props.errorChangingProducer?.ErrorMessage || props.errorChangingProducer?.Errors?.join(', ')}
                        </MessageBarBody>
                    </MessageBar>}
                    {props.Consumption?.producer && <div className="personCard">
                        <Avatar
                            aria-label={props.Consumption?.producer}
                            name={props.GetPersonByIdentifier(props.Consumption?.producer)?.fullName}
                            color="colorful"
                            size={40}
                        />
                        <div>
                            <Text>{props.GetPersonByIdentifier(props.Consumption?.producer)?.fullName}</Text>
                            <Label>{props.GetPersonByIdentifier(props.Consumption?.producer)?.speciality}</Label>
                        </div>
                    </div>}
                    {!props.Consumption?.producer && <Text className="infoValueEmpty">Sin profesional informado</Text>}
                    <div className="buttonGroup">
                        <Button appearance="outline" onClick={() => setChangeProducerOptionActive(true)}>{props.Consumption?.producer ? 'Cambiar profesional' : 'Informar profesional'}</Button>
                    </div>
                </div>}
                {changeProducerOptionActive &&
                    <div className="editPanel">
                        <PractitionerSelectorComponent MultiselectEnable={false} SelectedPractitioners={
                            newProducer ? [newProducer.userId] : props.Consumption?.producer ? [props.Consumption?.producer] : []
                        } onChangeSelection={(selectedPractitioner: Practitioner[] | undefined) => selectedPractitioner?.length ? setNewProducer(selectedPractitioner[0]) : setNewProducer(undefined)} />
                        <div className="buttonGroup">
                            <Button appearance="primary" disabled={props.isChangingProducer || newProducer === undefined} onClick={HandleChangeProducer}>
                                {props.isChangingProducer ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                            </Button>
                            <Button appearance="subtle" disabled={props.isChangingProducer} onClick={() => setChangeProducerOptionActive(false)}>Cancelar</Button>
                        </div>
                    </div>
                }
            </div>
            <Divider className="section-divider" />
            <Subtitle2 className="section-title">Concepto asociado</Subtitle2>
            <div className='RelatedConceptContainer'>
                {!changeConceptOptionActive && <div className="infoRow">
                    {props.failChangingConcept && <MessageBar key={props.Consumption?.id} intent={"error"}>
                        <MessageBarBody>
                            <MessageBarTitle>Asociación de concepto</MessageBarTitle>
                            {props.errorChangingConcept?.ErrorMessage || props.errorChangingConcept?.Errors?.join(', ')}
                        </MessageBarBody>
                    </MessageBar>}
                    <Text className={props.Consumption?.conceptDescription ? 'infoValue' : 'infoValueEmpty'}>
                        {props.Consumption?.conceptDescription ?? 'No asociado a ningún concepto'}
                    </Text>
                    <div className="buttonGroup">
                        <Button appearance="outline" onClick={() => setChangeConceptOptionActive(true)}>{props.Consumption?.contractConceptId ? 'Cambiar concepto' : 'Asociar concepto'}</Button>
                    </div>
                </div>}
                {changeConceptOptionActive && props.ContractId && <div className="editPanel">
                    <ContractConceptSelectorComponent
                        ContractId={props.ContractId}
                        referralDate={props.Consumption?.serviceDueDate}
                        onSelectedContractConcept={(contract: ContractConcept[]) => setNewConceptAssociation(contract[0])}
                        ContractConceptsSelected={props.Consumption?.contractConceptId ? [props.Consumption?.conceptDescription] : []}
                        MultiselectEnable={false}
                    />
                    <Checkbox
                        checked={ChangeAll}
                        onChange={(ev, data) => data.checked === true ? setChangeAll(data.checked) : setChangeAll(false)}
                        label="Aplicar a todos los consumos de este tipo"
                    />
                    <div className="buttonGroup">
                        <Button appearance="primary" disabled={props.isChangingConcept || newConceptAssociation === undefined} onClick={HandleChangeConcept}>
                            {props.isChangingConcept ? <Spinner size="tiny" label="" /> : 'Confirmar'}
                        </Button>
                        <Button appearance="subtle" disabled={props.isChangingConcept} onClick={() => setChangeConceptOptionActive(false)}>Cancelar</Button>
                    </div>
                </div>}
            </div>
            <div className='consumptionGrandTotal'>
                <div>
                    <Label>TOTAL A LIQUIDAR</Label>
                    <Text>{props.Consumption?.totalAmount?.toLocaleString('es-AR', { style: 'currency', currency: props.Consumption?.currencyCode?.toUpperCase() }) ?? '0.00'}</Text>
                </div>
                {props.Consumption?.haveFeeDivision && <>
                    <Divider />
                    <div>
                        <div>
                            <Label>División de honorarios</Label>
                        </div>
                        <div>
                            <Text><small>Centro </small> {(props.Consumption?.conceptValues?.reduce(
                                (acc, concept) => acc + concept.ownerAmount, 0
                            ) + props.Consumption?.ownerAmountWithholdingTotal).toLocaleString('es-AR', { style: 'currency', currency: props.Consumption?.currencyCode?.toUpperCase() }) ?? '0.00'}
                            </Text>
                            <Text><small>Equipo médico </small> {(props.Consumption?.conceptValues?.reduce(
                                (acc, concept) => acc + concept.teamAmount, 0
                            ) + props.Consumption?.teamAmountWithholdingTotal).toLocaleString('es-AR', { style: 'currency', currency: props.Consumption?.currencyCode?.toUpperCase() }) ?? '0.00'}</Text>
                        </div>
                    </div>
                    <div>
                        <MessageBar key={props.Consumption?.id} intent={'info'} style={{ width: '100%' }}>
                            <MessageBarBody>
                                <MessageBarTitle>WithHolding</MessageBarTitle>
                                Ya se ha descontando el monto ({props.Consumption?.withHolingPercentage + '%'}) &nbsp;{props.Consumption?.withHolingAmount.toLocaleString('es-AR', { style: 'currency', currency: props.Consumption?.currencyCode?.toUpperCase() }) ?? '0.00'} definido por contrato
                            </MessageBarBody>
                        </MessageBar>
                    </div>
                </>}

            </div>
            <Divider className="section-divider" />
            <DataGrid
                items={valueRows}
                columns={valueColumns}
                getRowId={(item) => item.id}
                className="valuesDataGrid"
                size="small"
            >
                <DataGridHeader>
                    <DataGridRow>
                        {({ renderHeaderCell }) => (
                            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody<ValueRow>>
                    {({ item, rowId }) =>
                        item.rowKind === 'section' ? (
                            <div key={rowId} className="valuesDataGrid-sectionRow">
                                <Text weight="semibold">{item.concept}</Text>
                            </div>
                        ) : (
                            <DataGridRow<ValueRow>
                                key={rowId}
                                className={`valuesDataGrid-row valuesDataGrid-row--${item.rowKind}`}
                            >
                                {({ renderCell }) => (
                                    <DataGridCell>{renderCell(item)}</DataGridCell>
                                )}
                            </DataGridRow>
                        )
                    }
                </DataGridBody>
            </DataGrid>
            {(props.Consumption?.groupingId !== undefined && props.Consumption?.groupingId !== null) && <>
                <Divider className="section-divider" />
                <Subtitle2 className="section-title">Conceptos relacionados</Subtitle2>
                <ChildConsumptionListComponent ProtocolId={props.ProtocolId} CosnumptionId ={props.Consumption?.id} />
            </>}
        </div >
    )
};


const ConsumptionDetailsCompoment = (props: ComponentFunctions.Props) => {

    const HandleCancelForm = () => {
        props.onClose();
    };

    const [showVisitConceptForm, setShowVisitConceptForm] = useState(false);

    useEffect(() => {
        if (props.isOpen && props.ProtocolId && props.ProtocolConsumptionId) {
            props.GetProtocolConsumptionById(props.ProtocolId, props.ProtocolConsumptionId);
        }
    }, [props.isOpen, props.ProtocolConsumptionId]);

    const Footer = (
        <DrawerFooter>
            <Button appearance="subtle" onClick={HandleCancelForm}>Cerrar</Button>
        </DrawerFooter>
    );

    return (
        <div>
            <OverlayDrawer
                position={'end'}
                open={props.isOpen}
                size='large'
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <>
                                {props.Consumption?.emrCrossVersionId !== undefined && Number(props.Consumption?.parentInstanceId) !== 0  && <Button appearance="primary" onClick={() => setShowVisitConceptForm(true)}>
                                    Ver visita
                                </Button>}
                                <Button
                                    style={{ marginLeft: '1em' }}
                                    appearance="subtle"
                                    aria-label="Close"
                                    icon={<Dismiss24Regular />}
                                    onClick={() => HandleCancelForm()}
                                />
                            </>
                        }
                    >
                        <div className='consumptionHeader'>
                            <CalendarToday28Regular style={{ height: '2.5em', width: '2.5em' }} />
                            <div>
                                <Subtitle1 style={{ textTransform: 'capitalize' }}>{props.Consumption?.serviceName}</Subtitle1>
                                <Text>{props.Consumption?.stageName}</Text>
                            </div>

                        </div>
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody className='ScrollShadow'>
                    {props.Consumption && <PanelInfo {...props} />}
                </DrawerBody>
                {Footer}
            </OverlayDrawer>
            {showVisitConceptForm === true && props.Consumption && props.Consumption?.patientNumberInProtocol && props.ContractId && <VisitConceptFormComponent
                ContractId={props.ContractId}
                isOpen={showVisitConceptForm}
                onClose={() => setShowVisitConceptForm(false)}
                protocolId={props.ProtocolId}
                visitId={Number(props.Consumption?.parentInstanceId)}
                patientId={props.Consumption?.patientNumberInProtocol}
            />}
        </div>
    );

}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionDetailsCompoment as any);

